import { useState } from "react"
import Seo from "@/components/Seo"
import { api } from "@/lib/api"
import { WhatsAppButtons } from "@/components/WhatsAppButtons"

const SERVICES_LIST = [
  "Network Infrastructure",
  "Cloud Solutions",
  "Cybersecurity",
  "IT Consulting",
  "Hardware Supply",
  "Managed IT Services",
  "Other",
]

const OFFICES = [
  {
    flag: "🇺🇸",
    city: "Minnesota, USA",
    type: "Global HQ",
    phone: "+1682-461-4255",
    email: "info@jero-onetechnologies.com",
  },
  {
    flag: "🇺🇬",
    city: "Kampala, Uganda",
    type: "Franchise",
    phones: ["+256 766 553 449", "+256 701 511 262", "+256 752 200 865"],
    address: "Downtown Entebbe · P.O. Box 171736, Kampala GPO",
    email: "info@jero-onetechnologies.com",
    email2: "support@jero-onetechnologies.com",
  },
  {
    flag: "🇨🇦",
    city: "Canada",
    type: "Franchise",
    email: "info@jero-onetechnologies.com",
  },
]

export function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    office: "",
    message: "",
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    setLoading(true); setError("")
    const parts = [form.message]
    if (form.company) parts.push(`\n\nCompany: ${form.company}`)
    if (form.office) parts.push(`\nNearest office: ${form.office}`)
    try {
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        subject: `${form.service || "Consultation"} request from ${form.name}`,
        message: parts.join(""),
        type: "contact",
      })
      setSubmitted(true)
    } catch {
      setError("Could not send your message. Please try again or email info@jero-onetechnologies.com.")
    } finally {
      setLoading(false)
    }
  }

  const inputCls =
    "w-full bg-[#111A14] border border-[#1B5E35]/20 text-[#F4F6F0] placeholder-[#6B7C6E] px-4 py-3 rounded-lg outline-none focus:border-[#1B5E35] transition-colors text-sm"

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif" }}>
      <Seo title="Contact Jero-One Technologies | Kampala, Entebbe & Minnesota" description="Call +256 766 553 449 or email info@jero-onetechnologies.com for IT solutions, quotes and support in Uganda, the USA and Canada. Free consultation." path="/contact" />
      {/* Hero */}
      <section className="grid md:grid-cols-2 min-h-[45vh]">
        <div className="bg-[#0A0F0B] flex flex-col justify-center px-8 md:px-16 py-16">
          <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-4">
            GET IN TOUCH
          </div>
          <h1
            className="text-6xl font-black text-[#F4F6F0] leading-none mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            LET'S START A<br />
            <span style={{ color: "#1B5E35" }}>CONVERSATION.</span>
          </h1>
          <p className="text-[#6B7C6E] leading-relaxed max-w-md">
            Whether you're in Minnesota, Kampala, or Canada — our team is ready
            to help. Fill out the form and we'll get back to you within one
            business day.
          </p>
        </div>
        <div className="bg-[#1B5E35] flex flex-col justify-center px-8 md:px-16 py-12 gap-6">
          {OFFICES.map((o) => (
            <div key={o.city} className="flex items-start gap-4">
              <span className="text-2xl flex-shrink-0">{o.flag}</span>
              <div>
                <div className="font-bold text-[#F4F6F0] text-sm">{o.city}</div>
                <div className="text-[10px] text-[#F4F6F0]/60 font-mono mb-1">
                  {o.type.toUpperCase()}
                </div>
                {"phone" in o && (
                  <a
                    href={`tel:${o.phone}`}
                    className="block text-[#F4F6F0]/70 text-xs hover:text-[#F4F6F0] transition-colors"
                  >
                    {o.phone}
                  </a>
                )}
                {o.phones?.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    data-testid={`contact-uganda-phone-${p.slice(-3)}`}
                    className="block text-[#F4F6F0]/70 text-xs hover:text-[#F4F6F0] transition-colors"
                  >
                    {p}
                  </a>
                ))}
                {o.phones && <WhatsAppButtons className="my-2" />}
                {o.address && (
                  <div className="text-[#F4F6F0]/60 text-xs">{o.address}</div>
                )}
                <a
                  href={`mailto:${o.email}`}
                  className="block text-[#F4F6F0]/70 text-xs hover:text-[#F4F6F0] transition-colors break-all"
                >
                  {o.email}
                </a>
                {o.email2 && (
                  <a
                    href={`mailto:${o.email2}`}
                    className="block text-[#F4F6F0]/70 text-xs hover:text-[#F4F6F0] transition-colors break-all"
                  >
                    {o.email2}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Form section */}
      <section className="py-20 px-6 md:px-14 bg-[#0A0F0B]">
        <div className="max-w-4xl mx-auto">
          {submitted ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-6">✅</div>
              <h2
                className="text-4xl font-black text-[#F4F6F0] mb-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                MESSAGE SENT
              </h2>
              <p className="text-[#6B7C6E] mb-8">
                Thank you — your message has reached our team and we'll reply
                within one business day.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-[#1B5E35] text-[#F4F6F0] font-bold px-8 py-3 rounded-lg hover:bg-[#2A7D4A] transition-colors text-sm"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-3">
                  SEND US A MESSAGE
                </div>
                <h2
                  className="text-5xl font-black text-[#F4F6F0]"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  FREE CONSULTATION REQUEST
                </h2>
              </div>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      FULL NAME *
                    </label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handle}
                      placeholder="Jane Smith"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      EMAIL ADDRESS *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handle}
                      placeholder="jane@company.com"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      PHONE NUMBER
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handle}
                      placeholder="+1 (612) 555-0100"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      COMPANY NAME
                    </label>
                    <input
                      name="company"
                      value={form.company}
                      onChange={handle}
                      placeholder="Acme Corp"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      SERVICE INTERESTED IN *
                    </label>
                    <select
                      name="service"
                      required
                      value={form.service}
                      onChange={handle}
                      className={inputCls + " appearance-none cursor-pointer"}
                    >
                      <option value="">Select a service…</option>
                      {SERVICES_LIST.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                      NEAREST OFFICE
                    </label>
                    <select
                      name="office"
                      value={form.office}
                      onChange={handle}
                      className={inputCls + " appearance-none cursor-pointer"}
                    >
                      <option value="">Select location…</option>
                      {OFFICES.map((o) => (
                        <option key={o.city} value={o.city}>
                          {o.flag} {o.city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#6B7C6E] font-mono tracking-widest block mb-2">
                    YOUR MESSAGE *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handle}
                    placeholder="Tell us about your IT challenges and what you're looking to achieve…"
                    className={inputCls + " resize-none"}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={loading}
                  className="w-full bg-[#1B5E35] hover:bg-[#2A7D4A] disabled:opacity-60 text-[#F4F6F0] font-bold py-4 rounded-lg transition-colors text-sm tracking-wider"
                >
                  {loading ? "SENDING…" : "SEND MESSAGE →"}
                </button>
                {error && <p className="text-center text-xs text-red-400" data-testid="contact-error">{error}</p>}
                <p className="text-center text-xs text-[#6B7C6E]">
                  We respond within 1 business day. Your information is kept
                  confidential.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 md:px-14 bg-[#111A14] border-t border-[#1B5E35]/10">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-4xl font-black text-[#F4F6F0] mb-10 text-center"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "How quickly can you respond to IT emergencies?",
                a: "Our managed services clients receive response within 1 hour for critical issues, 2 hours for high-priority, and 4 hours for standard requests — 24/7.",
              },
              {
                q: "Do you serve small businesses or only enterprises?",
                a: "We serve organizations of all sizes — from startups and SMEs to government agencies and multinational corporations.",
              },
              {
                q: "Can I get support from your Uganda office if I'm in the USA?",
                a: "Yes. All our offices operate on the same service standards and can serve clients internationally through our global network.",
              },
              {
                q: "How do I get started?",
                a: "Simply fill out the contact form above or call your nearest office. We'll schedule a free consultation within 24 hours.",
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="bg-[#0A0F0B] border border-[#1B5E35]/10 rounded-xl overflow-hidden group"
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-[#F4F6F0] text-sm flex items-center justify-between hover:text-[#2A7D4A] transition-colors">
                  {faq.q}
                  <span className="text-[#1B5E35] text-lg group-open:rotate-45 transition-transform flex-shrink-0 ml-4">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-5 text-[#6B7C6E] text-sm leading-relaxed border-t border-[#1B5E35]/10 pt-4">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
